import React, { useState, useCallback } from 'react';
import { invoiceAPI } from '../../../services/api';
import { PageHeader } from '../../../components/layout/PageHeader';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const fmt  = n => 'Rs.' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });
const fmtK = n => { const v=Number(n||0); return v>=100000?'Rs.'+(v/100000).toFixed(1)+'L':v>=1000?'Rs.'+(v/1000).toFixed(0)+'K':'Rs.'+v.toFixed(0); };
const COLORS = ['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export function SalesReport() {
  const now = new Date();
  const [from,    setFrom]    = useState(new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]);
  const [to,      setTo]      = useState(now.toISOString().split('T')[0]);
  const [data,    setData]    = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [topCust, setTopCust] = useState([]);
  const [topProd, setTopProd] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [salesRes, monthRes, custRes, prodRes] = await Promise.allSettled([
        invoiceAPI.reportSales({ from, to }),
        invoiceAPI.reportMonthly({ from, to }),
        invoiceAPI.reportTopCustomers({ from, to, limit: 8 }),
        invoiceAPI.reportTopProducts({ from, to, limit: 8 }),
      ]);
      if (salesRes.status === 'fulfilled') setData(salesRes.value.data);
      if (monthRes.status === 'fulfilled') setMonthly(monthRes.value.data || []);
      if (custRes.status  === 'fulfilled') setTopCust(custRes.value.data  || []);
      if (prodRes.status  === 'fulfilled') setTopProd(prodRes.value.data  || []);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [from, to]);

  const inp = 'px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';
  const lbl = 'block text-xs font-semibold text-gray-500 uppercase mb-1.5';
  const statusData = (data?.byStatus || []).map(s => ({ name: s.status?.replace('_',' '), value: Number(s.total||0), count: s.count }));

  return (
    <div className="space-y-5">
      <PageHeader title="Sales Report" subtitle="Invoice sales summary, monthly trends, top customers and products" />

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex gap-4 flex-wrap items-end">
          <div><label className={lbl}>From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)} className={inp}/></div>
          <div><label className={lbl}>To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)} className={inp}/></div>
          <div className="flex gap-2 flex-wrap">
            {[
              { label:'This Month', f:new Date(now.getFullYear(),now.getMonth(),1), t:now },
              { label:'This Year',  f:new Date(now.getFullYear(),0,1), t:now },
              { label:'FY 25-26',   f:new Date('2025-04-01'), t:new Date('2026-03-31') },
            ].map(r=>(
              <button key={r.label} onClick={()=>{setFrom(r.f.toISOString().split('T')[0]);setTo(r.t.toISOString().split('T')[0]);}}
                className="px-3 py-2 text-xs font-medium border border-gray-200 rounded-xl hover:bg-indigo-50 text-gray-600">{r.label}</button>
            ))}
          </div>
          <button onClick={load} disabled={loading} className="px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {loading ? 'Loading...' : 'Generate'}
          </button>
        </div>
      </div>

      {!data && !loading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
          <DocumentChartBarIcon className="w-12 h-12 mx-auto text-gray-200 mb-3"/>
          <p className="text-gray-400 text-sm">Select date range and click Generate</p>
        </div>
      )}

      {loading && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"/></div>}

      {data && !loading && <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label:'Total Invoices', value:data.totalInvoices, color:'text-indigo-600' },
            { label:'Total Sales',    value:fmtK(data.totalSales),       color:'text-indigo-600', sub:fmt(data.totalSales) },
            { label:'Collected',      value:fmtK(data.totalCollected),   color:'text-green-600',  sub:fmt(data.totalCollected) },
            { label:'Outstanding',    value:fmtK(data.totalOutstanding), color:'text-amber-600',  sub:fmt(data.totalOutstanding) },
          ].map(k=>(
            <div key={k.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{k.label}</p>
              <p className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
              {k.sub && <p className="text-xs text-gray-400 mt-0.5">{k.sub}</p>}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label:'Avg Invoice', value:fmtK(data.averageInvoice) },
            { label:'Total Tax',   value:fmtK(data.totalTax) },
            { label:'Discounts',   value:fmtK(data.totalDiscount) },
            { label:'Taxable Amt', value:fmtK(data.totalTaxable) },
          ].map(k=>(
            <div key={k.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{k.label}</p>
              <p className="text-lg font-bold text-gray-800 mt-1">{k.value}</p>
            </div>
          ))}
        </div>

        {monthly.length > 0 && (
          <Card title="Monthly Sales Trend">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthly.map(m=>({ month:m.month, Sales:m.totalSales, Collected:m.totalCollected, Outstanding:m.totalOutstanding }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="month" tick={{fontSize:11}}/>
                <YAxis tickFormatter={v=>fmtK(v)} tick={{fontSize:11}}/>
                <Tooltip formatter={v=>fmt(v)}/>
                <Legend/>
                <Bar dataKey="Sales"       fill="#6366f1" radius={[3,3,0,0]}/>
                <Bar dataKey="Collected"   fill="#22c55e" radius={[3,3,0,0]}/>
                <Bar dataKey="Outstanding" fill="#f59e0b" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-5">
          {statusData.length > 0 && (
            <Card title="Sales by Invoice Status">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={statusData} cx="45%" cy="50%" outerRadius={75} dataKey="value"
                    label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                    {statusData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                  </Pie>
                  <Tooltip formatter={v=>fmt(v)}/>
                </PieChart>
              </ResponsiveContainer>
            </Card>
          )}

          {topCust.length > 0 && (
            <Card title="Top Customers by Sales">
              <div className="space-y-2.5">
                {topCust.map((c,i)=>{
                  const pct = topCust[0]?.totalSales > 0 ? (c.totalSales/topCust[0].totalSales)*100 : 0;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 w-4 text-right">{i+1}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-0.5">
                          <span className="font-medium text-gray-700 truncate max-w-36">{c.customer?.name || 'Unknown'}</span>
                          <span className="text-indigo-600 font-semibold ml-2">{fmtK(c.totalSales)}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 rounded-full" style={{width:pct+'%'}}/>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 w-12 text-right">{c.invoiceCount} inv</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>

        {topProd.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-800 text-sm">Top Products / Services by Revenue</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>{['#','Product/Service','HSN','Qty Sold','Avg Rate','Total Revenue'].map(h=>(
                  <th key={h} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase text-left last:text-right">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {topProd.map((p,i)=>(
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-400 text-xs">{i+1}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{p.productName}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-400">{p.hsnCode||'—'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{p.totalQty} {p.unit}</td>
                    <td className="px-4 py-2.5 text-gray-600">{fmt(p.avgRate)}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-indigo-600">{fmt(p.totalRevenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>}
    </div>
  );
}
